import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';

import { quantileLabel } from '@/lib/quantiles';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, QuantileSummary } from '@/services/types';

/**
 * Quantiles on a LOGARITHMIC axis.
 *
 * Not a stylistic choice. On the measured corpus p10 is R$440 and the maximum is
 * R$144.6M -- six orders of magnitude. Drawn linearly this is a flat line with a
 * single spike, which tells a reader nothing about the 8,195 fines underneath.
 * The payload says so itself: `spread_ratio` arrives with a note instructing a
 * log scale whenever p90 exceeds ten times the median.
 */
export function FineDistributionPanel({
  answer,
  titleKey = 'anatel:overview.fines.title',
  helpKey = 'anatel:overview.fines.help',
  countKey = 'anatel:overview.fines.count',
}: {
  answer: AnswerOut<QuantileSummary>;
  /** Each agency names and scopes its own money column: ANATEL's fines that
      stand, ANEEL's penalties on resolved cases. Same shape, different claim. */
  titleKey?: string;
  helpKey?: string;
  /** The observation-count sentence names the unit too: "fines" here, and
      "penalties" where the column is ANEEL's -- a fine is one kind of penalty
      and the ANEEL corpus carries warnings and embargoes beside it. */
  countKey?: string;
}) {
  const { t, i18n } = useTranslation(['anatel', 'aneel', 'common']);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const exact = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(titleKey)}
      </h2>
      <Answer answer={answer} help={t(helpKey)}>
        {(summary) => {
          const data = Object.entries(summary.quantiles).map(([key, value]) => ({
            label: quantileLabel(t, key),
            value,
          }));
          return (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {t(countKey, { n: summary.n.toLocaleString(i18n.language) })}
              </p>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    {/*
                      `interval={0}` is load-bearing. Recharts drops axis ticks
                      that would overlap, and with five bars in a half-width
                      card it dropped exactly one: the MEDIAN -- the only bar a
                      reader is likely to quote. The chart then showed five bars
                      under four labels, so counting from either end gave the
                      wrong answer, and the field guide beside it defined a term
                      ("Typical (median)") that appeared nowhere on screen.
                      Forcing every tick costs a smaller font and is worth it.
                    */}
                    <XAxis
                      dataKey="label"
                      interval={0}
                      tick={<WrappedTick />}
                      height={34}
                      className="fill-muted-foreground"
                    />
                    <YAxis
                      scale="log"
                      domain={['auto', 'auto']}
                      allowDataOverflow
                      tickFormatter={(v: number) => currency.format(v)}
                      tick={{ fontSize: 11 }}
                      width={72}
                      className="fill-muted-foreground"
                    />
                    <Tooltip
                      formatter={(v: number) => exact.format(v)}
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                      }}
                    />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-muted-foreground">min</dt>
                <dd className="text-right tabular-nums text-foreground">
                  {exact.format(summary.minimum)}
                </dd>
                <dt className="text-muted-foreground">max</dt>
                <dd className="text-right tabular-nums text-foreground">
                  {exact.format(summary.maximum)}
                </dd>
              </dl>
            </div>
          );
        }}
      </Answer>
    </section>
  );
}
/**
 * Axis labels on two lines.
 *
 * `interval={0}` above is right and stays -- but forcing all five ticks into a
 * half-width card made the plain-language labels physically COLLIDE: measured
 * 2026-09-09 on /aneel/overview, "Quarto inferior" ran into "Típica (mediana)"
 * by 5px and that into "Quarto superior" by 4px, so the axis rendered as
 * `Quarto inferiorTípica (mediana)Quarto superior`. The median was illegible for
 * the same reason it was previously missing, which is the failure the comment
 * above exists to prevent -- the earlier fix moved that failure rather than
 * removing it.
 *
 * Wrapping at the space each label already contains halves the width every tick
 * needs. It keeps all five ticks, the full wording and the 10px size, so it
 * costs none of the three things the alternatives cost: dropping a tick,
 * shortening "Típica (mediana)" to jargon a lawyer would have to decode, or
 * angling the text.
 *
 * Not reachable by a component test: Recharts renders no children under jsdom's
 * zero-size layout, so this is verified in a real browser instead.
 */
function WrappedTick({
  x,
  y,
  payload,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
}) {
  const words = String(payload?.value ?? '').split(' ');
  const split = Math.ceil(words.length / 2);
  const lines =
    words.length > 1 ? [words.slice(0, split).join(' '), words.slice(split).join(' ')] : words;
  return (
    <text x={x} y={y} dy={10} textAnchor="middle" fontSize={10} className="fill-muted-foreground">
      {lines.map((line, index) => (
        <tspan key={line} x={x} dy={index === 0 ? 0 : 12}>
          {line}
        </tspan>
      ))}
    </text>
  );
}
